import { z } from "zod/v4"
import { CreatedAt, FirestoreTimestampSchema, UpdatedAt } from "./system"
import { PersonalNotificationId } from "../id"
import { DocumentSnapshotLike } from "../firestore"
import { BulkDeliveryMasterKind } from "./bulk-delivery-master"
import { PersonalNotificationIcon } from "./personal-notification-icon"

export const PersonalNotificationKind = z.enum(["info", "present"])

export type PersonalNotificationKindType = z.infer<typeof PersonalNotificationKind>

export const PersonalNotificationSourceKind = {
  BulkDelivery: "bulk_delivery",
  SubscriptionItemGrant: "subscription_item_grant",
  WelcomeDelivery: "welcome_delivery",
  // Manual: "manual",  // 将来の手動配信用
} as const

export type PersonalNotificationSourceKindType = typeof PersonalNotificationSourceKind[keyof typeof PersonalNotificationSourceKind]

export const PersonalNotificationSource = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal(PersonalNotificationSourceKind.BulkDelivery),
    bulk_delivery_master_id: z.string().describe("BulkDeliveryMasterId"),
    bulk_delivery_master_kind: z.enum(BulkDeliveryMasterKind).describe("BulkDeliveryMasterの種別"),
  }),
  z.object({
    kind: z.literal(PersonalNotificationSourceKind.SubscriptionItemGrant),
    subscription_item_grant_master_id: z.string().describe("SubscriptionItemGrantMasterId"),
  }),
  z.object({
    kind: z.literal(PersonalNotificationSourceKind.WelcomeDelivery),
    welcome_delivery_master_id: z.string().describe("WelcomeDeliveryMasterId"),
  }),
  // z.object({
  //   kind: z.literal(PersonalNotificationSourceKind.Manual),
  // }),
])

export const PersonalNotificationDeliveryContent = z.object({
  received_at: z.number().optional().describe("受け取った日時（存在すれば受け取り済み、なければ未受取）"),
  expire_at: z.number().describe("受け取り期限（必須）"),
})

export const UserPersonalNotificationMeta = z.object({
  kind: PersonalNotificationKind.optional().describe("通知の種別（info: 一般通知, present: プレゼント通知）"),
  title: z.string().describe("タイトル"),
  icon: PersonalNotificationIcon.describe("アイコン"),
  content: z.any().describe("QuillDelta形式のリッチテキスト"),
  scheduled_delivery_at: z.number().describe("管理ツールで設定された配信開始日時 (UNIX Timestamp) - これをユーザー向けに表示する"),
  source: PersonalNotificationSource.describe("この通知がどこから配信されたか"),
  delivery_content: PersonalNotificationDeliveryContent.nullable().describe("アイテムやコインがある場合は値が入る"),
})

export type UserPersonalNotificationMeta = z.infer<typeof UserPersonalNotificationMeta>

export const UserPersonalNotificationDocumentBody = UserPersonalNotificationMeta.extend({
  is_read: z.boolean().describe("既読フラグ"),
  read_at: FirestoreTimestampSchema.nullable().describe("既読日時"),
  reference_managed_image_ids: z.array(z.string()).describe("使用している画像のIDリスト（自動計算、BulkDeliveryMasterから引き継ぎ）"),
  created_at: CreatedAt,
  updated_at: UpdatedAt,
})

export type UserPersonalNotificationDocumentBody = z.infer<typeof UserPersonalNotificationDocumentBody>

export type UserPersonalNotification = UserPersonalNotificationDocumentBody & {
  id: PersonalNotificationId
}

export class UserPersonalNotificationDocuments {
  static toDocumentData(notification: UserPersonalNotification): object {
    return {
      ...notification,
      // id は document path に入るので入れない
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): UserPersonalNotification | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: PersonalNotificationId(doc.id),
      kind: data["kind"],
      title: data["title"],
      content: data["content"],
      icon: data["icon"],
      scheduled_delivery_at: data["scheduled_delivery_at"],
      source: data["source"],
      is_read: data["is_read"],
      read_at: data["read_at"],
      delivery_content: data["delivery_content"],
      reference_managed_image_ids: data["reference_managed_image_ids"] || [],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}
